import { Alert, Button, Col, Input, Row, Spinner2, Switch, Text } from 'components';
import { Api, formatLocale, theme } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const RULES = [
  { key: 'joinRequiresAccess', copy: 'join' },
  { key: 'sendRequiresAccess', copy: 'send' },
  { key: 'startRequiresAccess', copy: 'start' }
];

/** The highest open-chat cap the server accepts. */
const LIMIT_MAX = 1000;

/** The cap typed in the field: empty is no cap (null), anything not a whole 1–1000 is undefined. */
const parseLimit = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const limit = Number(trimmed);
  return limit >= 1 && limit <= LIMIT_MAX ? limit : undefined;
};

const limitText = (limit) => (limit == null ? '' : String(limit));

/**
 * The team's three FanChat 2.0 access switches (FR-006, FR-006a).
 *
 * Each decides whether one action needs FanChat access; with it off, any signed-in fan may do it.
 * Turning a switch on is confirmed first because it puts the paywall in front of fans who could do
 * that a moment ago. Turning one off only lets more fans in, so it saves straight away.
 *
 * Below them, the most chats one fan may keep open on this team. Empty, the default, is no cap
 * (QA, 3 Nov 2026); it saves with its own button, as typing a number is not a choice yet.
 */
const FanChatAccessPanel = ({ team }) => {
  const { t } = useTranslation();
  const [access, setAccess] = useState(null);
  const [status, setStatus] = useState('loading');
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [limit, setLimit] = useState('');

  useEffect(() => {
    if (!team?._id) return;

    let cancelled = false;
    setStatus('loading');

    (async () => {
      const res = await Api.get(`/admin/fanchatRooms/getAccess?teamId=${team._id}`);
      if (cancelled) return;

      if (!res?.data) return setStatus('error');

      setAccess(res.data);
      setLimit(limitText(res.data.openTopicLimit));
      setStatus('ready');
    })();

    return () => {
      cancelled = true;
    };
  }, [team?._id]);

  // The switch moves at once; a failed save puts it back. Api has already toasted the error.
  const save = async (key, value) => {
    const previous = access;
    const next = { ...access, [key]: value };

    setAccess(next);
    setSaving(true);

    const res = await Api.put('/admin/fanchatRooms/updateAccess', {
      teamId: team._id,
      joinRequiresAccess: next.joinRequiresAccess,
      sendRequiresAccess: next.sendRequiresAccess,
      startRequiresAccess: next.startRequiresAccess
    });

    setAccess(res?.data || previous);
    setSaving(false);
  };

  const parsedLimit = parseLimit(limit);
  const limitChanged = parsedLimit !== (access?.openTopicLimit ?? null);

  const saveLimit = async () => {
    if (saving || parsedLimit === undefined || !limitChanged) return;

    setSaving(true);

    const res = await Api.put('/admin/fanchatRooms/updateAccess', {
      teamId: team._id,
      joinRequiresAccess: access.joinRequiresAccess,
      sendRequiresAccess: access.sendRequiresAccess,
      startRequiresAccess: access.startRequiresAccess,
      openTopicLimit: parsedLimit
    });

    if (res?.data) {
      setAccess(res.data);
      setLimit(limitText(res.data.openTopicLimit));
    }
    setSaving(false);
  };

  const handleToggle = (rule) => {
    if (saving) return;
    if (access[rule.key]) return save(rule.key, false);

    setConfirming(rule);
  };

  const lastChange = () => {
    if (!access.updatedAt) return t('admin.fanChatAccess.usingDefaults');

    const date = formatLocale(access.updatedAt, 'LLL do yyy, hh:mm a');

    return access.updatedBy
      ? t('admin.fanChatAccess.lastChangedBy', { username: access.updatedBy.username, date })
      : t('admin.fanChatAccess.lastChanged', { date });
  };

  if (!team?._id) return null;

  return (
    <Col pad="24px" pos="relative">
      <Text h3>{t('admin.fanChatAccess.title')}</Text>
      <Text b2 col={theme.GREY_60} marg="4px 0 0">
        {t('admin.fanChatAccess.description')}
      </Text>

      {status === 'loading' && (
        <Row centerAll marg="32px 0">
          <Spinner2 col={theme.SECONDARY} />
        </Row>
      )}

      {status === 'error' && (
        <Text med col={theme.SECONDARY} marg="24px 0 0">
          {t('admin.fanChatAccess.loadFailed')}
        </Text>
      )}

      {status === 'ready' && (
        <>
          <Col noFlex wid="100%" marg="16px 0 0">
            {RULES.map((rule) => (
              <Row
                key={rule.key}
                between
                center
                wid="100%"
                gap="16px"
                pad="16px 0"
                style={{ borderBottom: `1px solid ${theme.GREY_20}` }}
              >
                <Col noFlex minWid="0">
                  <Text med>{t(`admin.fanChatAccess.${rule.copy}.label`)}</Text>
                  <Text c1 col={theme.GREY_60} marg="2px 0 0">
                    {t(`admin.fanChatAccess.${rule.copy}.hint`)}
                  </Text>
                </Col>

                <Switch
                  label={t(`admin.fanChatAccess.${rule.copy}.label`)}
                  activated={access[rule.key]}
                  onClick={() => handleToggle(rule)}
                />
              </Row>
            ))}

            <Row
              between
              center
              wid="100%"
              gap="16px"
              pad="16px 0"
              style={{ borderBottom: `1px solid ${theme.GREY_20}` }}
            >
              <Col noFlex minWid="0">
                <Text med>{t('admin.fanChatAccess.limit.label')}</Text>
                <Text c1 col={theme.GREY_60} marg="2px 0 0">
                  {t('admin.fanChatAccess.limit.hint')}
                </Text>
              </Col>

              <Row noFlex center gap="8px">
                <Col noFlex wid="120px">
                  <Input
                    aria-label={t('admin.fanChatAccess.limit.label')}
                    inputMode="numeric"
                    value={limit}
                    placeholder={t('admin.fanChatAccess.limit.none')}
                    onChange={(e) => setLimit(e.target.value)}
                  />
                </Col>
                <Button
                  small
                  text={t('admin.fanChatAccess.limit.save')}
                  onClick={saveLimit}
                  disabled={saving || parsedLimit === undefined || !limitChanged}
                />
              </Row>
            </Row>

            {parsedLimit === undefined && (
              <Text c1 col={theme.SECONDARY} marg="8px 0 0">
                {t('admin.fanChatAccess.limit.invalid', { max: LIMIT_MAX })}
              </Text>
            )}
          </Col>

          <Text c1 col={theme.GREY_60} marg="16px 0 0">
            {lastChange()}
          </Text>
        </>
      )}

      {confirming && (
        <Alert
          text={t(`admin.fanChatAccess.${confirming.copy}.confirm`)}
          acceptText={t('admin.fanChatAccess.turnOn')}
          refuseText={t('admin.fanChatAccess.cancel')}
          accept={() => {
            setConfirming(null);
            save(confirming.key, true);
          }}
          refuse={() => setConfirming(null)}
        />
      )}
    </Col>
  );
};

export default FanChatAccessPanel;
